import React from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  BarChart3, 
  Settings, 
  Shield, 
  Cpu, 
  Database,
  Wrench,
  FileText,
  ArrowRight,
  CheckCircle,
  TrendingUp
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Activity,
      title: 'Asset Health Monitoring',
      description: 'Real-time condition monitoring and health assessment using advanced IoT sensors and analytics.',
      features: [
        'Continuous vibration monitoring',
        'Temperature and thermal imaging',
        'Acoustic emission analysis',
        'Oil analysis integration',
        'Real-time alerting system',
        'Predictive health scoring'
      ],
      useCases: [
        'Critical rotating equipment monitoring',
        'Heat exchanger performance tracking',
        'Motor and bearing condition assessment',
        'Pump and compressor health monitoring'
      ]
    },
    {
      icon: BarChart3,
      title: 'Lifecycle Cost Analysis',
      description: 'Comprehensive TCO modeling and optimization to maximize asset value throughout its operational life.',
      features: [
        'Total Cost of Ownership (TCO) modeling',
        'Replacement vs. repair analysis',
        'Maintenance cost optimization',
        'Capital planning support',
        'Risk-adjusted cost projections',
        'ROI and NPV calculations'
      ],
      useCases: [
        'Equipment replacement planning',
        'Maintenance strategy optimization',
        'Capital expenditure justification',
        'Asset portfolio optimization'
      ]
    },
    {
      icon: Settings,
      title: 'Predictive Maintenance Strategy',
      description: 'AI-driven maintenance planning and scheduling to prevent failures before they occur.',
      features: [
        'Machine learning algorithms',
        'Failure prediction models',
        'Maintenance scheduling optimization',
        'Resource allocation planning',
        'Performance benchmarking',
        'Continuous strategy refinement'
      ],
      useCases: [
        'Production line optimization',
        'Critical equipment protection',
        'Maintenance team productivity',
        'Inventory optimization'
      ]
    },
    {
      icon: Shield,
      title: 'Criticality & Risk Assessments',
      description: 'Strategic asset prioritization and risk mitigation to focus resources on what matters most.',
      features: [
        'Asset criticality ranking',
        'Risk probability analysis',
        'Business impact assessment',
        'Failure mode identification',
        'Risk mitigation strategies',
        'Priority matrix development'
      ],
      useCases: [
        'Maintenance budget allocation',
        'Safety risk management',
        'Production continuity planning',
        'Regulatory compliance support'
      ]
    },
    {
      icon: Cpu,
      title: 'Digital Twin Development',
      description: 'Virtual asset modeling and simulation for enhanced decision-making and optimization.',
      features: [
        'Physics-based modeling',
        'Real-time data integration',
        'Scenario simulation',
        'Performance optimization',
        'Virtual testing capabilities',
        'Predictive analytics integration'
      ],
      useCases: [
        'Process optimization',
        'Design validation',
        'Training simulations',
        'What-if analysis'
      ]
    },
    {
      icon: Database,
      title: 'CMMS Integration',
      description: 'Seamless maintenance management system integration to enhance existing workflows.',
      features: [
        'Multi-platform compatibility',
        'Data synchronization',
        'Workflow automation',
        'Custom reporting',
        'API development',
        'Legacy system integration'
      ],
      useCases: [
        'Maintenance workflow optimization',
        'Data consolidation',
        'Reporting standardization',
        'System modernization'
      ]
    },
    {
      icon: Wrench,
      title: 'Failure Mode & RCA Workshops',
      description: 'Root cause analysis and failure prevention through structured problem-solving methodologies.',
      features: [
        'FMEA facilitation',
        'Root cause analysis',
        'Problem-solving workshops',
        'Team training',
        'Action plan development',
        'Follow-up monitoring'
      ],
      useCases: [
        'Chronic failure resolution',
        'Process improvement',
        'Team capability building',
        'Quality enhancement'
      ]
    },
    {
      icon: FileText,
      title: 'Audit & Optimization Reporting',
      description: 'Performance insights and improvement recommendations through comprehensive audits.',
      features: [
        'Comprehensive assessments',
        'Performance benchmarking',
        'Gap analysis',
        'Improvement roadmaps',
        'KPI development',
        'Progress tracking'
      ],
      useCases: [
        'Operational excellence',
        'Compliance verification',
        'Performance improvement',
        'Strategic planning'
      ]
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              Comprehensive Asset Management <span className="text-[#233790]">Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Transform your operations with our full suite of industrial asset management services, 
              designed to maximize performance, minimize risks, and optimize lifecycle costs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mr-4">
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-light text-gray-900">{service.title}</h2>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <CheckCircle className="w-5 h-5 text-[#233790] mr-2" />
                        Key Features
                      </h3>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="text-gray-600 flex items-start">
                            <div className="w-2 h-2 bg-[#233790] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <TrendingUp className="w-5 h-5 text-[#233790] mr-2" />
                        Use Cases
                      </h3>
                      <ul className="space-y-2">
                        {service.useCases.map((useCase, idx) => (
                          <li key={idx} className="text-gray-600 flex items-start">
                            <div className="w-2 h-2 bg-[#233790] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            {useCase}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={`/images/service-${index + 1}.png`}
                      alt={service.title}
                      className="w-full h-80 object-contain rounded-lg"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Ready to Optimize Your Asset Performance?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Let our experts assess your current asset management practices and develop 
              a customized strategy for your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors"
              >
                Schedule Consultation
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
              >
                View Case Studies <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;